import { useQuery } from "@tanstack/react-query";
import { MatchesQueryKeys } from "./matchesQueryKeys";
import {
  getScoremerSearchRaces,
  getScoremerTeamRaces,
  ScoremerSearchSport,
  searchScoremerTeams,
} from "@/api/matches/scoremerSearch";

// Every call reaches a third-party catalogue, so both queries are held
// for a while and never refetched on focus: an operator flipping between
// tabs must not re-trigger a provider fan-out.
const SEARCH_STALE_MS = 5 * 60 * 1000;

// Shorter than the search itself — a race list changes as matches play.
const RACES_STALE_MS = 60 * 1000;

// `name` shorter than the server's own minimum is never sent — the
// endpoint would refuse it, and a one-letter search is not a question
// worth asking a third-party catalogue.
export const useScoremerTeamSearch = (
  name: string,
  sportType: ScoremerSearchSport
) =>
  useQuery({
    queryKey: [MatchesQueryKeys.ScoremerTeams, sportType, name],
    queryFn: ({ signal }) => searchScoremerTeams(name, sportType, signal),
    enabled: name.trim().length >= 2,
    staleTime: SEARCH_STALE_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

export const useScoremerTeamRaces = (
  teamId: number | null,
  sportType: ScoremerSearchSport
) =>
  useQuery({
    queryKey: [MatchesQueryKeys.ScoremerTeamRaces, sportType, teamId],
    queryFn: ({ signal }) =>
      getScoremerTeamRaces(teamId as number, sportType, signal),
    enabled: teamId !== null,
    staleTime: RACES_STALE_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

// The merged view. Enabled only while the operator has it open, so a search
// never pays for a list nobody asked to see.
export const useScoremerSearchRaces = (
  name: string,
  enabled: boolean,
  sportType: ScoremerSearchSport
) =>
  useQuery({
    queryKey: [MatchesQueryKeys.ScoremerSearchRaces, sportType, name],
    queryFn: ({ signal }) => getScoremerSearchRaces(name, sportType, signal),
    enabled: enabled && name.trim().length >= 2,
    staleTime: RACES_STALE_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });
